import { kebabCase } from 'lodash/fp';
import { useCallback, useMemo, VFC } from 'react';

import Accordion from 'dashboard/components/Accordion';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { getTokensToDisplayValue, UsableObjectConfig } from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionCreateTransactionV1 } from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface ParamsCreateTransactionV1Props {
  transactionTypes: WorkflowActionMeta['transaction-types'];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionCreateTransactionV1['params'];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (
    newParams: WorkflowActionCreateTransactionV1['params'],
    template?: TemplateFilter,
  ) => void;
}

const ParamsCreateTransactionV1: VFC<ParamsCreateTransactionV1Props> = ({
  transactionTypes,
  editable,
  errors,
  params,
  usableObjects,
  updateActionParams,
}) => {
  const hasTransactionTypePerm = usePermission(Permission.TransactionTypeView);

  const handleEditInputFields = (value: Record<string, string>) => {
    updateActionParams({
      ...params,
      'transaction-fields': Object.keys(value).reduce<Record<string, string>>(
        (acc: Record<string, string>, elem: string) => {
          acc[elem] = value[elem];
          return acc;
        },
        {},
      ),
    });
  };

  const selectTransactionType = useCallback(
    (value: string) => {
      updateActionParams(
        {
          ...params,
          'transaction-type-token': value,
          'transaction-fields': {},
        },
        { type: ApiModel.Transaction, id: value },
      );
    },
    [params, updateActionParams],
  );

  const transactionTypeOptions = useMemo(
    () => transactionTypes.map((txnType) => ({ label: txnType.name, value: txnType.token })),
    [transactionTypes],
  );

  const fieldOptions = useMemo(() => {
    const txnType = transactionTypes.find(
      (txnType) => txnType.token === params['transaction-type-token'],
    );
    return (txnType?.fieldSchemas || []).reduce(
      (acc, fs) => {
        acc[kebabCase(fs.key)] = fs.label || fs.key;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [params, transactionTypes]);

  const [showEditPanel, { toggle: toggleTransactionFields, disable: closeTransactionFields }] =
    useToggle(false);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionCreateTransactionV1['params']>(errors);

  const transactionTypeHelpText = hasTransactionTypePerm
    ? params['transaction-type-token']
      ? undefined
      : 'The current transaction type is not available to you at this time.'
    : 'Your role does not have permissions to view transaction types.';

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Transaction Type'
        htmlFor='params-create-transaction__transaction-type'
        helpText={transactionTypeHelpText}
      />
      <Select
        value={params['transaction-type-token']}
        onValueChange={selectTransactionType}
        id='params-create-transaction__transaction-type'
        disabled={!editable || !hasTransactionTypePerm}
        hasError={hasErrorFor('transaction-type-token')}
      >
        {transactionTypeOptions.map(({ label, value }) => (
          <Option key={`${label}-${value}`} value={value}>
            {label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('transaction-type-token')}
      <Spacer height={2} />
      <Accordion title='Transaction Fields' defaultExpanded>
        {showEditPanel && Object.keys(fieldOptions).length > 0 && (
          <FieldMappingsPanel
            title='Transaction fields'
            usableObjects={usableObjects}
            fieldMappings={params['transaction-fields'] ?? {}}
            presetFields={fieldOptions}
            removable={false}
            onOutsideClick={closeTransactionFields}
            onUpdateFieldMappings={handleEditInputFields}
          />
        )}
        <Spacer height={2} />
        <FieldsList
          fields={params['transaction-fields']}
          fieldKey='transaction-fields'
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          editable={
            editable && params['transaction-type-token'] && Object.keys(fieldOptions).length > 0
          }
          toggleEditPanel={toggleTransactionFields}
          tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
          placeholderEmptyText={
            params['transaction-type-token']
              ? Object.keys(fieldOptions).length > 0
                ? 'No fields selected'
                : 'Transaction type does not have any fields configured'
              : 'No transaction type selected'
          }
          errors={errors}
        />
      </Accordion>
    </>
  );
};

export default ParamsCreateTransactionV1;
